import { useCallback, useEffect, useState } from 'react';
import Dropdown from '../components/Dropdown';
import Icon from '../components/Icon';
import { ErrorBanner } from '../components/Feedback';
import { api } from '../api/client';
import { present } from '../absent';

const ACTIONS = [
  { value: '', label: 'Any action' },
  { value: 'VIEW', label: 'Viewed' },
  { value: 'STREAM', label: 'Streamed' },
  { value: 'DOWNLOAD', label: 'Downloaded' },
];

const ROLES = [
  { value: '', label: 'Any role' },
  { value: 'TEACHER', label: 'Teachers' },
  { value: 'STUDENT', label: 'Students' },
  { value: 'SUPER_ADMIN', label: 'Super Admins' },
];

const PAGE_SIZE = 50;

/**
 * Module 10's Access Log Viewer: who accessed what, when, and from where.
 *
 * <p>Two things are deliberate here. The default window is a week, because this table
 * grows by a row per resource opened and "everything" is a question nobody actually has.
 * And the tamper check is a button rather than something the page runs on load — it
 * reads every chained entry, and an answer nobody asked for is not worth that.
 */
export default function AccessLogPage() {
  const [filters, setFilters] = useState(() => lastDays(7));
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verification, setVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.accessLogs({ ...filters, page, size: PAGE_SIZE }));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Empty strings are kept rather than dropped: the query builder already skips them,
  // and turning a cleared date into undefined would flip a controlled input to
  // uncontrolled mid-edit.
  function set(name, value) {
    setPage(0);
    setFilters((current) => ({ ...current, [name]: value }));
  }

  async function verify() {
    setVerifying(true);
    setError(null);
    try {
      setVerification(await api.verifyAccessLog());
    } catch (err) {
      setError(err);
    } finally {
      setVerifying(false);
    }
  }

  const rows = data?.items ?? [];

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>Access Log</h1>
          <p>Every resource opened, by whom, when and from where.</p>
        </div>
        <button type="button" className="btn btn-secondary" disabled={verifying} onClick={verify}>
          <Icon name="shield" size={16} /> {verifying ? 'Checking…' : 'Check for tampering'}
        </button>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {verification && <Verdict result={verification} />}

      <section className="card card-pad" style={{ marginBottom: 'var(--s1)' }}>
        <div className="toolbar">
          <input
            type="date"
            className="input"
            style={{ flex: '0 1 150px' }}
            aria-label="From date"
            value={filters.from}
            max={filters.to}
            onChange={(event) => set('from', event.target.value)}
          />
          <input
            type="date"
            className="input"
            style={{ flex: '0 1 150px' }}
            aria-label="To date"
            value={filters.to}
            min={filters.from}
            onChange={(event) => set('to', event.target.value)}
          />
          <div style={{ flex: '0 1 170px' }}>
            <Dropdown id="roleFilter" options={ROLES} value={filters.role}
                      onChange={(role) => set('role', role)} ariaLabel="Filter by role" />
          </div>
          <div style={{ flex: '0 1 170px' }}>
            <Dropdown id="actionFilter" options={ACTIONS} value={filters.action}
                      onChange={(action) => set('action', action)} ariaLabel="Filter by action" />
          </div>
          <input
            className="input"
            style={{ flex: '1 1 200px' }}
            placeholder="Search a person, resource or book"
            aria-label="Search"
            maxLength={100}
            defaultValue={filters.q}
            onKeyDown={(event) => {
              if (event.key === 'Enter') set('q', event.currentTarget.value.trim());
            }}
            onBlur={(event) => set('q', event.target.value.trim())}
          />
        </div>
      </section>

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Who</th>
                <th>Resource</th>
                <th>Book</th>
                <th>Action</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="mono">{new Date(row.at).toLocaleString()}</td>
                  <td>
                    {row.userName}
                    <div className="tiny muted">{roleName(row.userRole)}</div>
                  </td>
                  <td>{row.resourceName}</td>
                  <td>{row.titleName ?? '—'}</td>
                  <td>{actionName(row.action)}</td>
                  <td className="mono tiny">
                    {row.ip ?? '—'}
                    {row.deviceId && <div className="muted">{row.deviceId}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div className="empty">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="empty">Nothing was opened in this period.</div>
        )}

        {data && data.totalPages > 1 && (
          <div className="row-between" style={{ padding: 'var(--s1)' }}>
            <span className="tiny muted">
              Page {data.page + 1} of {data.totalPages} · {data.totalItems.toLocaleString()} entries
            </span>
            <div className="toolbar">
              <button type="button" className="btn btn-secondary btn-sm" disabled={data.page === 0}
                      onClick={() => setPage((current) => Math.max(current - 1, 0))}>
                Previous
              </button>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!data.hasNext}
                      onClick={() => setPage((current) => current + 1)}>
                Next
              </button>
            </div>
          </div>
        )}
      </section>

      <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
        Entries are written once and never edited. Each one carries the hash of the entry
        before it, so an alteration anywhere is visible — that is what the check above
        reads. Timestamps are shown in your local time; the log itself is kept in UTC.
      </p>
    </>
  );
}

/**
 * The verdict, stated plainly. An audit screen that hedges is useless: either the chain
 * holds or it does not, and the entries it could not check are named separately rather
 * than folded into either answer.
 */
function Verdict({ result }) {
  const problems = result.problems ?? [];
  return (
    <div className={`verdict ${result.intact ? 'verdict-ok' : 'verdict-bad'}`}>
      <span className="verdict-mark">
        <Icon name={result.intact ? 'check' : 'shield'} size={20} />
      </span>
      <div>
        <h2>
          {result.intact
            ? 'The log has not been altered'
            : `${result.problemCount} problem${result.problemCount === 1 ? '' : 's'} found`}
        </h2>
        <p>
          Checked {result.chainedEntries.toLocaleString()} entr
          {result.chainedEntries === 1 ? 'y' : 'ies'}
          {/* present(), not !== null: a chain with nothing in it omits the field, and
              !== null was true for it — then .toLocaleString() threw and took the
              page down rather than printing a range. */}
          {present(result.firstSequence) &&
            ` (${result.firstSequence.toLocaleString()} to ${result.lastSequence.toLocaleString()})`}
          {result.purgedThrough > 0 &&
            ` · ${result.purgedThrough.toLocaleString()} older entries removed by the retention policy`}
          {result.unchainedEntries > 0 &&
            ` · ${result.unchainedEntries.toLocaleString()} older entries predate this check and cannot be verified`}
          .
        </p>
        {problems.length > 0 && (
          <ul className="tiny" style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {problems.slice(0, 10).map((problem, index) => (
              <li key={`${problem.sequence}-${problem.kind}-${index}`}>
                Entry {problem.sequence}: {problem.detail}
              </li>
            ))}
          </ul>
        )}
        {result.problemCount > problems.length && (
          <p className="tiny" style={{ marginTop: 6 }}>
            Only the first {problems.length} problems are listed. Treat the whole chain
            from the first break onward as unreliable.
          </p>
        )}
      </div>
    </div>
  );
}

function lastDays(days) {
  const to = new Date();
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - (days - 1));
  return { from: iso(from), to: iso(to), role: '', action: '', q: '' };
}

function iso(date) {
  return date.toISOString().slice(0, 10);
}

function roleName(role) {
  return {
    SUPER_ADMIN: 'Super Admin',
    SUPPORT_EXECUTIVE: 'Support Executive',
    SALES_REP: 'Sales Representative',
    TEACHER: 'Teacher',
    STUDENT: 'Student',
  }[role] ?? role;
}

function actionName(action) {
  return { VIEW: 'Viewed', STREAM: 'Streamed', DOWNLOAD: 'Downloaded' }[action] ?? action;
}
